import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiService } from '../../services/apiService';
import { Show } from '../../types/Show';
import { cachingStrategyService, DataType } from '../../services/cachingStrategyService';
import { getCacheCoordinationService } from '../../services/cacheCoordinationService';
import { debugLog } from '../../utils/debugLog';

export const SHOWS_QUERY_KEY = ['shows'] as const;

export const useShowsQuery = () => {
  const queryClient = useQueryClient();
  
  // Get optimized caching strategy for past shows data
  const cacheStrategy = cachingStrategyService.getStrategy(DataType.PAST_SHOWS_DATA);
  
  const query = useQuery({
    queryKey: SHOWS_QUERY_KEY,
    queryFn: async (): Promise<Show[]> => {
      const response = await ApiService.getShows();
      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch shows');
      }
      return response.data;
    },
    // Apply centralized caching strategy for past shows data
    staleTime: cacheStrategy.staleTime,
    gcTime: cacheStrategy.gcTime,
    refetchOnWindowFocus: cacheStrategy.refetchOnWindowFocus,
    refetchOnReconnect: cacheStrategy.refetchOnReconnect,
    retry: cacheStrategy.retry,
    retryDelay: cacheStrategy.retryDelay,
  });

  // Handle successful data fetch with useEffect
  React.useEffect(() => {
    if (query.data && query.isSuccess) {
      const coordinationService = getCacheCoordinationService(queryClient);
      
      // Synchronize individual caches with fresh bulk data
      coordinationService.synchronizeIndividualCaches(query.data);
      
      // Prefetch popular shows for better cache hit rates
      coordinationService.prefetchPopularShows(query.data, 5);
      
      // Record successful bulk cache hit
      coordinationService.recordCacheHit('bulk');
      
      debugLog('🎯 Bulk shows cache updated and coordinated with individual caches');
    }
  }, [query.data, query.isSuccess, queryClient]);

  return query;
};
